import { Button, Flex, Grid, GridItem, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from 'assets/icons/carousel_arrow_left.svg';
import ElementsImage from 'assets/images/elements.png';
import { UPGRADE_REDIRECT_BIZ_LINK, UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, LEFT_MENU_OPENED, UPGRADE } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { setActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSlice';
import { useAppDispatch } from 'modules/Editor/store/hooks';
import { UserType, UserTypes } from 'modules/User';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { ElementThumbnail } from '../ElementsMenu/ElementThumbnail';
import { ListMenuPack, SignatureMenuPack, TextBlockMenuPack } from '../ElementsMenu/ElementsMenu.components.lazy';
import { ELEMENTS_PACKS } from '../ElementsMenu/ElementsMenu.config';
import { ElementsPackTypes } from '../ElementsMenu/ElementsMenu.types';

const TEXT_ELEMENTS = [ElementsPackTypes.TextBlocks, ElementsPackTypes.Signatures, ElementsPackTypes.Lists];

const UPGRADE_REDIRECT_LINKS_BY_TYPE: { [key: string]: string } = {
  [UserTypes.Free]: UPGRADE_REDIRECT_LINK,
  [UserTypes.Premium]: UPGRADE_REDIRECT_BIZ_LINK,
};

interface TextElementsMenuProps {
  selectedElementPack: ElementsPackTypes | null;
  setSelectedElementPack: (pack: ElementsPackTypes | null) => void;
  userType: UserType;
  textWidgetMenuRef?: React.RefObject<HTMLDivElement>;
}

const MENU_HEADER_OFFSET = 74;

export const TextElementsMenu = ({
  selectedElementPack,
  setSelectedElementPack,
  userType,
  textWidgetMenuRef,
}: TextElementsMenuProps): ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('editor_widget_menu');
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
  const [selectedModalUpgradePlan, setSelectedModalUpgradePlan] = useState<UserType>(UserTypes.Premium);

  const onClickOnPack = (selected: ElementsPackTypes) => {
    const validPacks = ELEMENTS_PACKS.map((pack) => pack.id);
    if (!validPacks.includes(selected)) return;
    setSelectedElementPack(selected);
  };

  const onClickViewAll = useCallback((): void => {
    Mixpanel.track(LEFT_MENU_OPENED.eventName, {
      from: LEFT_MENU_OPENED.from.LeftPanel,
      type: LEFT_MENU_OPENED.type.Elements,
    });

    dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.ELEMENTS));
  }, [dispatch]);

  const onReturnToPacks = () => {
    setSelectedElementPack(null);
  };

  const onClickUpgradeModal = useCallback(
    (upgradePlan?: UserType) => {
      if (upgradePlan) setSelectedModalUpgradePlan(upgradePlan);

      onOpenUpgradeModal();
      Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
        Feature: UPGRADE.feature.Elements,
        'User Plan': getUserPlanProperty(userType),
      });
    },
    [onOpenUpgradeModal, userType],
  );

  const onGated = useCallback(() => {
    Mixpanel.track(UPGRADE.eventName, {
      Feature: UPGRADE.feature.Elements,
      'User Plan': getUserPlanProperty(userType),
    });

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.Elements, 'elements widget');
    window.open(UPGRADE_REDIRECT_LINKS_BY_TYPE[userType] || UPGRADE_REDIRECT_LINK, '_self');
  }, [userType]);

  const onClickUpgrade = useCallback(onGated, [onGated]);

  return (
    <Flex direction='column' gap='8px' mt={selectedElementPack ? 0 : 3}>
      {!selectedElementPack && (
        <Flex alignItems='center' justifyContent='space-between' mb={2} pl={1}>
          <Text fontSize='sm' fontWeight='semibold'>
            {t('sideMenu.textMenu.elementsHeading')}
          </Text>
          <Button
            variant='ghost'
            fontSize='sm'
            fontWeight='medium'
            color='upgrade.blue.500'
            size='sm'
            onClick={onClickViewAll}
          >
            {t('sideMenu.textMenu.elementsViewMore')}
          </Button>
        </Flex>
      )}
      <Grid templateColumns={'repeat(2, 1fr)'} columnGap={4} rowGap={4} position='relative'>
        {ELEMENTS_PACKS.filter((pack) => TEXT_ELEMENTS.includes(pack.id)).map((pack, index) => {
          const { id, thumbnail } = pack;
          return (
            <GridItem key={index} display={!!selectedElementPack ? 'none' : 'grid'}>
              <ElementThumbnail
                id={id}
                index={index}
                onClick={() => onClickOnPack(id)}
                thumbnail={thumbnail}
                caption={t(`sideMenu.elementsMenu.${`${id.toString()}`}` as keyof typeof t)}
              />
            </GridItem>
          );
        })}
      </Grid>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={selectedModalUpgradePlan}
        modalImg={ElementsImage}
        modalType={ModalUpgradeTypeEnum.Elements}
      />
      {!!selectedElementPack && (
        <>
          <Flex alignItems={'center'} h={30}>
            <IconButton
              variant='ghost'
              size={'xs'}
              icon={<LeftArrowIcon />}
              aria-label={'Go back to elements menu list'}
              onClick={onReturnToPacks}
              autoFocus={true}
            />
            <Text as='h2' ml={2} fontSize='sm' color='font.500' fontWeight='semibold'>
              {t(`sideMenu.elementsMenu.${`${selectedElementPack.toString()}`}` as keyof typeof t)}
            </Text>
          </Flex>
          <Flex
            overflowX='hidden'
            overflowY='auto'
            pt={3}
            maxHeight={
              textWidgetMenuRef?.current ? `${textWidgetMenuRef.current.offsetHeight - MENU_HEADER_OFFSET}px` : '100%'
            }
          >
            {selectedElementPack === ElementsPackTypes.TextBlocks && (
              <TextBlockMenuPack onClickUpgradeModal={onClickUpgradeModal} />
            )}
            {selectedElementPack === ElementsPackTypes.Signatures && (
              <SignatureMenuPack onClickUpgradeModal={onClickUpgradeModal} />
            )}
            {selectedElementPack === ElementsPackTypes.Lists && (
              <ListMenuPack onClickUpgradeModal={onClickUpgradeModal} />
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
};

TextElementsMenu.displayName = 'TextElementsMenu';
